import React, { useEffect, useMemo, useRef, useState } from "react";
import { MdAdd, MdUpload } from "react-icons/md";
import AddCustomerForm, { type CustomerFormValues } from "./AddCustomerForm";
import type { Customer } from "../../../redux/slices/customersSlice";

const SMS_CUSTOMER_LIMIT = 50;

interface CustomerListPanelProps {
  customers: Customer[];
  customersLoading: boolean;
  showAddCustomerForm: boolean;
  onShowAddCustomerForm: () => void;
  onHideAddCustomerForm: () => void;
  onSaveCustomer: (values: CustomerFormValues) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  medium: string;
  onSelectedCustomersChange?: (customers: Customer[]) => void;
}

const CustomerListPanel: React.FC<CustomerListPanelProps> = ({
  customers,
  customersLoading,
  showAddCustomerForm,
  onShowAddCustomerForm,
  onHideAddCustomerForm,
  onSaveCustomer,
  onFileUpload,
  medium,
  onSelectedCustomersChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const isSms = medium.toLowerCase() === "sms";

  const validCustomers = useMemo(
    () =>
      customers.filter((customer) => {
        const hasName = Boolean(customer.name?.trim());

        if (isSms) {
          return hasName || Boolean(customer.phone?.trim());
        }

        return hasName || Boolean(customer.email?.trim());
      }),
    [customers, isSms],
  );

  const states = useMemo(
    () =>
      Array.from(
        new Set(validCustomers
            .map((customer) => customer.address?.state?.trim())
            .filter((state): state is string => Boolean(state)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [validCustomers],
  );

  const cities = useMemo(
    () =>
      Array.from(
        new Set(validCustomers.filter(
              (customer) =>
                !selectedState ||
                customer.address?.state?.trim() === selectedState,
            )
            .map((customer) => customer.address?.city?.trim())
            .filter((city): city is string => Boolean(city)),
        ),).sort((a, b) => a.localeCompare(b)),
    [selectedState, validCustomers],
  );

  const filteredCustomers = useMemo(
    () =>
      validCustomers.filter((customer) => {const matchesState = !selectedState || customer.address?.state?.trim() === selectedState;
        const matchesCity = !selectedCity || customer.address?.city?.trim() === selectedCity;
        return matchesState && matchesCity;
      }),
    [selectedCity, selectedState, validCustomers],
  );

  const selectedCustomers = useMemo(
    () =>
      validCustomers.filter((customer) =>
        selectedCustomerIds.has(String(customer.id)),
      ),
    [selectedCustomerIds, validCustomers],
  );

  const remainingSmsCount = SMS_CUSTOMER_LIMIT - selectedCustomerIds.size;
  const reachedSmsLimit = remainingSmsCount === 0;
  const selectableVisibleCustomers = filteredCustomers.filter((customer) => !selectedCustomerIds.has(String(customer.id)),);
  const allVisibleSelected = filteredCustomers.length > 0 && filteredCustomers.every((customer) => selectedCustomerIds.has(String(customer.id)), );

  useEffect(() => {
    if (!isSms) { setSelectedCustomerIds(new Set()); setSelectedState(""); setSelectedCity(""); }
  }, [isSms]);

  useEffect(() => {
    setSelectedCity("");
  }, [selectedState]);

  useEffect(() => {
    const availableIds = new Set( validCustomers.map((customer) => String(customer.id)));

    setSelectedCustomerIds((current) => {const next = new Set(
        [...current].filter((customerId) => availableIds.has(customerId)),
    );

    return next.size === current.size ? current : next;
    });
  }, [validCustomers]);

  useEffect(() => {
    onSelectedCustomersChange?.(selectedCustomers);
  }, [onSelectedCustomersChange, selectedCustomers]);

  const toggleCustomer = (customerId: string) => {
    setSelectedCustomerIds((current) => {
      const next = new Set(current);

      if (next.has(customerId)) {
        next.delete(customerId);
      } else if (next.size < SMS_CUSTOMER_LIMIT) {
        next.add(customerId);
      }

      return next;
    });
  };

  const toggleAllVisibleCustomers = () => {
    setSelectedCustomerIds((current) => {
      const next = new Set(current);

      if (allVisibleSelected) {
        filteredCustomers.forEach((customer) => {
          next.delete(String(customer.id));
        });

        return next;
      }

      selectableVisibleCustomers
        .slice(0, SMS_CUSTOMER_LIMIT - next.size)
        .forEach((customer) => {
          next.add(String(customer.id));
        });

      return next;
    });
  };

  return (
    <div className="rounded-2xl border border-gray-700 bg-[#2a2a2a] p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">
          {isSms ? "SMS Recipients" : "Email Recipients"}{" "}
          {validCustomers.length > 0 && `(${validCustomers.length})`}
        </h3>

        <div className="flex items-center gap-2">
          <button type="button" onClick={onShowAddCustomerForm} 
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-brand-primary text-white transition-colors hover:bg-[#ee4423]" title="Add Customer">
            <MdAdd size={20} />
          </button>

          <input ref={fileInputRef} type="file" accept=".csv,.xlsx" onChange={onFileUpload} className="hidden" />

          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={customersLoading}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-gray-600 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Upload Customers">
            {customersLoading ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <MdUpload size={20} />
            )}
          </button>
        </div>
      </div>

      <p className="mb-4 text-sm text-gray-400">
        These customers will receive your {isSms ? "SMS" : "email"} campaigns
      </p>

      {showAddCustomerForm ? (
        <AddCustomerForm onSave={onSaveCustomer} onCancel={onHideAddCustomerForm} />
      ) : customersLoading ? (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-brand-primary" />
          <p className="text-sm text-gray-400">Loading customers...</p>
        </div>
      ) : isSms && validCustomers.length > 0 ? (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <select
                value={selectedState}
                onChange={(event) => setSelectedState(event.target.value)}
                className="h-9 min-w-32 rounded-lg border border-gray-700 bg-[#1a1a1a] px-3 text-sm text-white outline-none transition-colors focus:border-brand-primary"
                aria-label="Filter customers by state"
              >
                <option value="">All states</option>
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>

              <select
                value={selectedCity}
                onChange={(event) => setSelectedCity(event.target.value)}
                className="h-9 min-w-32 rounded-lg border border-gray-700 bg-[#1a1a1a] px-3 text-sm text-white outline-none transition-colors focus:border-brand-primary"
                aria-label="Filter customers by city"
              >
                <option value="">All cities</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* <span className="ml-2 text-xs text-gray-500">
            {selectedCustomerIds.size}/50 selected
          </span> */}

          {filteredCustomers.length > 0 ? (
            <div className="max-h-96 overflow-y-auto rounded-lg bg-[#1a1a1a]">
              <div className="sticky top-0 grid grid-cols-[100px_1.2fr_1fr_1fr_1fr] gap-3 border-b border-gray-700 bg-[#1a1a1a] p-3">
                {/* <div className="flex items-center justify-center">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisibleCustomers} className="h-4 w-4 cursor-pointer accent-[#EE4423]" aria-label="Select all filtered customers" />
                </div> */}
                <div className="text-xs font-semibold uppercase text-gray-400">
                  Name
                </div>
                <div className="text-xs font-semibold uppercase text-gray-400">
                  Phone
                </div>
                <div className="text-xs font-semibold uppercase text-gray-400">
                  State
                </div>
                <div className="text-xs font-semibold uppercase text-gray-400">
                  City
                </div>
              </div>

              {filteredCustomers.map((customer, index) => {
                const customerId = String(customer.id);
                const isSelected = selectedCustomerIds.has(customerId);
                const selectionDisabled = reachedSmsLimit && !isSelected;

                return (
                  <label
                    key={customerId}
                    className={`grid grid-cols-[100px_1.2fr_1fr_1fr_1fr] gap-3 p-3 transition-colors ${
                      index !== filteredCustomers.length - 1
                        ? "border-b border-gray-700/50"
                        : ""
                    } ${
                      selectionDisabled
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:bg-[#252525]"
                    }`}
                  >
                    {/* <div className="flex items-center justify-center">
                      <input type="checkbox" checked={isSelected} disabled={selectionDisabled} onChange={() => toggleCustomer(customerId)}
                        className="h-4 w-4 cursor-pointer accent-[#EE4423] disabled:cursor-not-allowed"
                      />
                    </div> */}
                    <div className="truncate text-sm text-white">
                      {customer.name || "-"}
                    </div>
                    <div className="truncate text-sm text-gray-400">
                      {customer.phone || "-"}
                    </div>
                    <div className="truncate text-sm text-gray-400">
                      {customer.address?.state || "-"}
                    </div>
                    <div className="truncate text-sm text-gray-400">
                      {customer.address?.city || "-"}
                    </div>
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500">
                No customers match the selected filters
              </p>
            </div>
          )}
        </div>
      ) : validCustomers.length > 0 ? (
        <div className="max-h-96 overflow-y-auto">
          <div className="sticky top-0 grid grid-cols-2 gap-4 rounded-t-lg border-b border-gray-700 bg-[#1a1a1a] p-3">
            <div className="text-xs font-semibold uppercase text-gray-400">
              Name
            </div>
            <div className="text-xs font-semibold uppercase text-gray-400">
              Email
            </div>
          </div>

          <div className="rounded-b-lg bg-[#1a1a1a]">
            {validCustomers.map((customer, index) => (
              <div key={customer.id}
                className={`grid grid-cols-2 gap-4 p-3 transition-colors hover:bg-[#252525] ${
                  index !== validCustomers.length - 1
                    ? "border-b border-gray-700/50"
                    : ""
                }`}
              >
                <div className="truncate text-sm text-white">
                  {customer.name || "-"}
                </div>
                <div className="truncate text-sm text-gray-400">
                  {customer.email || "-"}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="py-8 text-center">
          <p className="text-sm text-gray-500">No customers found</p>
          <p className="mt-1 text-xs text-gray-600">
            Add customers to start sending {isSms ? "SMS" : "email"} campaigns
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomerListPanel;